import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import DailyIframe from "@daily-co/daily-js";
import VideoCallPage from "./tavus/VideoCallPage";

const ChatOngoingVideoCallComponent = forwardRef(({ url, onUserLeave, onMeetingJoined }, ref) => {
  const wrapperRef = useRef(null);
  const dailyRef = useRef(null);
  const [isCallActive, setIsCallActive] = useState(true);
  const [isExplicitlyEnded, setIsExplicitlyEnded] = useState(false);
  const isComponentMounted = useRef(true);
  const callInstanceRef = useRef(null);

  // Centralized cleanup function
  const cleanupCallResources = async () => {
    // Remove event listeners
    window.removeEventListener("focus", handleWindowFocus);
    window.removeEventListener("blur", handleWindowBlur);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    // Restore default body styles
    document.body.style.margin = "";
    document.body.style.padding = "";
    document.body.style.overflow = "";
    document.body.style.height = "";
    document.body.style.width = "";
    document.documentElement.style.height = "";
    document.documentElement.style.width = "";
    document.documentElement.style.overflow = "";
    // Leave and destroy Daily instance
    if (callInstanceRef.current) {
      try {
        await callInstanceRef.current.leave();
      } catch (e) {}
      try {
        await callInstanceRef.current.destroy();
      } catch (e) {}
      callInstanceRef.current = null;
      dailyRef.current = null;
    }
    // Failsafe: stop all active media tracks
    if (window && window.navigator && window.navigator.mediaDevices) {
      if (window.navigator.mediaDevices._streams) {
        // Some browsers may keep a reference to streams
        window.navigator.mediaDevices._streams.forEach(stream => {
          if (stream && stream.getTracks) {
            stream.getTracks().forEach(track => track.stop());
          }
        });
      }
    }
    // Extra failsafe: stop all tracks from all video/audio elements
    const mediaElements = document.querySelectorAll('video, audio');
    mediaElements.forEach(el => {
      if (el.srcObject && el.srcObject.getTracks) {
        el.srcObject.getTracks().forEach(track => track.stop());
        el.srcObject = null;
      }
    });
  };

  // Expose cleanup to parent via ref
  useImperativeHandle(ref, () => ({
    forceCleanup: cleanupCallResources
  }));

  // Window event handlers must be defined outside cleanup for reference
  function handleWindowFocus() {
    console.log("Window focused, maintaining call connection");
  }
  function handleWindowBlur() {
    console.log("Window blurred, maintaining call connection");
  }
  function handleVisibilityChange() {
    console.log("Visibility changed, maintaining call connection");
  }

  useEffect(() => {
    isComponentMounted.current = true;
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.body.style.overflow = "hidden";
    document.body.style.height = "100vh";
    document.body.style.width = "100vw";
    document.documentElement.style.height = "100vh";
    document.documentElement.style.width = "100vw";
    document.documentElement.style.overflow = "hidden";

    if (!url || !wrapperRef.current) return;

    const createDailyInstance = async () => {
      try {
        dailyRef.current = DailyIframe.createFrame(wrapperRef.current, {
          showLeaveButton: true,
          iframeStyle: {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: "none",
          },
          showFullscreenButton: true,
          showLocalVideo: true,
        });
        callInstanceRef.current = dailyRef.current;
        dailyRef.current.on("left-meeting", (event) => {
          if (onUserLeave) {
            onUserLeave({
              event: "user_left_meeting",
              timestamp: new Date().toISOString(),
              details: event,
              explicitlyEnded: isExplicitlyEnded
            });
          }
        });
        dailyRef.current.on("call-ended", (event) => {
          setIsCallActive(false);
          if (onUserLeave) {
            onUserLeave({
              event: "call_ended",
              timestamp: new Date().toISOString(),
              details: event,
              explicitlyEnded: false
            });
          }
        });
        await dailyRef.current.join({ url });
        if (onMeetingJoined) {
          onMeetingJoined();
        }
      } catch (error) {
        console.error("Error setting up Daily:", error);
      }
    };
    createDailyInstance();
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("blur", handleWindowBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      cleanupCallResources();
    };
  }, [url, onUserLeave, onMeetingJoined, isExplicitlyEnded]);

  // Handle explicit call ending
  const handleExplicitEndCall = () => {
    setIsExplicitlyEnded(true);
    cleanupCallResources();
    if (onUserLeave) {
      onUserLeave({
        event: "user_explicitly_ended_call",
        timestamp: new Date().toISOString(),
        explicitlyEnded: true
      });
    }
  };

  return (
    <>
      <VideoCallPage 
        url={url} 
        onJoin={onMeetingJoined} 
        onLeave={handleExplicitEndCall} 
      />
      <div ref={wrapperRef} style={{ display: 'none' }} />
    </>
  );
});

export default ChatOngoingVideoCallComponent;
